/* Journey to Horizon doom track (Hope & Fear campaign frame materials),
   laid out like the printed sheet. Marked boxes are saved in this browser
   (localStorage) and a nested box opens only once the box above it is marked. */

import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import HandoutSheet from '../../components/HandoutSheet';

const STORAGE_KEY = 'dhtools-doom-track';

interface DoomEvent {
  text: string;
  nested?: DoomEvent[];
}

interface DoomTier {
  title: string;
  effect: string;
  adversaryFeature?: { name: string; type: string; cost: string; text: string };
  events: DoomEvent[];
}

const group = (text: string, ...nested: string[]): DoomEvent => ({ text, nested: nested.map((n) => ({ text: n })) });

const doomTiers: DoomTier[] = [
  {
    title: 'TIER 1: DARK HARBINGERS',
    effect: 'After you mark 5 boxes in this tier, when you gain Fear at the end of a long rest, you roll twice and take the higher result.',
    events: [
      group('Bandits and marauders harry travelers.', 'Tensions rise over contested resources.', 'A mysteriously empty sanctuary is discovered.', 'A sanctuary seals itself off once again.'),
      group('A new settlement is overtaken by surging wilderness.', 'Chimeric dire beasts reveal their presence.', 'Swarms of overgrown insects darken the sky.', 'Wild magic shatters magic and crumbles stone.', 'Terrana supercharges magic dangerously and unpredictably.'),
      group('A shadowblighted orchard turns to ash overnight.', 'Desiccated and blackened birds fall dead from the sky.', 'Shadowblighted echoes of lost loved ones appear.'),
      group("Travelers trade stories of Seeker sightings.", "The party suffers visions of the Lovers' re-emergence.", 'An Aurelian shrine lies broken and desecrated.', 'The party finds signs of a Seeker.', 'The party finds remnants of a cult ritual.', 'A cultist spy tracks the party.', 'The Vessel attracts attention - and suspicion.'),
    ],
  },
  {
    title: 'TIER 2: RISING THREATS',
    effect: "After you mark 5 boxes in this tier, when you generate a region and the result on the d20 is a 1, treat it as if you'd rolled 1 twice in a row.",
    events: [
      group('Roving bands of professional soldiers demand tribute at swordpoint.', 'A demagogue rises to power with promises of prosperity.', 'A noble demands fealty from their neighbors.', 'A prominent figure in the Aurelian Order persecutes the unfaithful.', 'Civilians are drafted in preparation for war.'),
      group('Chaotic weather kills the harvest.', 'Tidal waves and tornadoes batter Valloren.', 'Dire beasts and walking carnivorous plants attack.', 'A fungal plague spreads through the land.', 'Food stores are spoiled by pestilence.', 'The ground cracks open, releasing a toxic miasma.'),
      group('Shadowblight swamps a sanctuary, trapping its inhabitants.', "A reservoir's water turns into tar.", 'Sorrowblighted humanoids emerge from hidden settlements.'),
      group("A Seeker crosses the party's path.", 'An irreplaceable repository of Old Valloren knowledge is destroyed.', 'Numerous Aurelian priests are slain in a coordinated attack.', 'A Seeker chases the party, forcing them to flee.', "An enemy discovers the Vessel's true nature."),
    ],
  },
  {
    title: 'TIER 3: IMMINENT DESTRUCTION',
    effect: 'After you mark 5 boxes in this tier, adversaries gain the following feature:',
    adversaryFeature: { name: 'Dark-Hearted', type: 'Passive', cost: 'Spend a Fear', text: 'to give the adversary advantage on its next roll.' },
    events: [
      group('War is declared, and each sanctuary is called to claim a side.', 'A sanctuary falls to internal strife.', 'A vast military force sweeps through the continent.', 'A sanctuary is besieged, then falls to military force.'),
      group('An explosion of verdant abundance renders regions impassable.', 'An ancient creature awakens from millennia of dreamless slumber.', 'Unleashed terrana drives people into animalistic fury.', 'Spellcasting triggers magical exhaustion.', 'Beasts of legend leave their lairs to terrorize the population.'),
      group('Color drains from the world.', 'Victims of the Long Sorrow crawl from their graves.', 'Entire fields, forests, jungles, and wetlands fall to rot.', 'Unrestrained shadowblight triggers inescapable despair.'),
      group('A Seeker corners the party, forcing them to fight.', 'A powerful personage reveals their Cult allegiance.', 'A defeated Seeker reappears with newfound abilities or allies.', 'An army of cultists sweeps across Valloren culling sanctuaries.'),
      { text: 'The Vessel is lost or stolen.' },
    ],
  },
  {
    title: 'TIER 4: FINAL CATACLYSM',
    effect: 'Each time you mark a box from this tier, you gain a number of Fear equal to the number of marked boxes in this tier.',
    events: [
      { text: 'A group of Seekers ambushes the party.' },
      { text: 'Valloren is riven by an impassable mire of shadowblight.' },
      { text: 'A tyrant claims the crown of Valloren.' },
      { text: 'An assassin mortally wounds a party member.' },
      { text: 'In pockets of reality, time warps and bends in on itself.' },
      { text: 'A wave of wild magic sweeps across the continent, transforming everything it touches into overgrown, uncanny forms.' },
      { text: 'Whole sanctuaries are terrorized by colossal agents of the earth, raging at its exploitation.' },
      { text: 'The sky turns black forever, plunging Valloren into endless midnight.' },
      { text: 'One by one, sanctuaries slip under the surface of an infinite sea of shadowblight.' },
      {
        text: 'The veil of death is torn asunder, spewing shambling corpses and unclean spirits into the mortal realm.',
        nested: [{ text: 'The Lovers awaken and begin weaving the Second Sorrow.', nested: [{ text: 'The Vessel is destroyed.' }] }],
      },
    ],
  },
];

/** Saved marks, as event paths ("tier title/event text/nested text"). */
function loadMarks(): Set<string> {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return new Set(Array.isArray(saved) ? saved.filter((m): m is string => typeof m === 'string') : []);
  } catch {
    return new Set();
  }
}

interface DoomEventsProps {
  events: DoomEvent[];
  path: string;
  open: boolean;
  marks: Set<string>;
  onToggle: (id: string) => void;
}

function DoomEvents({ events, path, open, marks, onToggle }: DoomEventsProps) {
  return (
    <ul className="hf-doom__events">
      {events.map((e) => {
        const id = `${path}/${e.text}`;
        const marked = marks.has(id);
        return (
          <li key={e.text}>
            <label>
              <input type="checkbox" checked={marked} disabled={!open} onChange={() => onToggle(id)} />
              <span>{e.text}</span>
            </label>
            {e.nested && <DoomEvents events={e.nested} path={id} open={marked} marks={marks} onToggle={onToggle} />}
          </li>
        );
      })}
    </ul>
  );
}

export default function DoomTrack() {
  const [marks, setMarks] = useState<Set<string>>(loadMarks);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...marks])); } catch { /* storage blocked */ }
  }, [marks]);

  // Unmarking a box also clears the boxes nested under it.
  const toggle = (id: string) => setMarks((prev) => {
    const next = new Set(prev);
    if (next.has(id)) [...next].filter((m) => m === id || m.startsWith(`${id}/`)).forEach((m) => next.delete(m));
    else next.add(id);
    return next;
  });

  const reset = () => {
    if (window.confirm('Clear every marked box on the Doom Track?')) setMarks(new Set());
  };

  return (
    <HandoutSheet
      id="doom-track"
      frame="Journey to Horizon"
      title="Doom Track"
      className="hf-doom"
      aside={(
        <div className="hf-doom__intro">
          <p>The <strong>Doom Track</strong> represents growing disasters that befall New Valloren if the party doesn't resurrect Aurelia. At the end of each session, mark a box from the party's tier or below. You can't mark a nested box until you mark the box it's nested under.</p>
          <button type="button" className="hf-doom__reset" onClick={reset} disabled={marks.size === 0}>
            <RotateCcw size={12} aria-hidden="true" /> Reset track
          </button>
        </div>
      )}
    >
      <div className="hf-doom__tiers">
        {doomTiers.map((tier) => (
          <div key={tier.title} className="hf-doom__tier">
            <h3 className="hf-banner"><span className="hf-banner__plate"><span>{tier.title}</span></span></h3>
            <p>{tier.effect}</p>
            {tier.adversaryFeature && (
              <p>
                <strong><em>{tier.adversaryFeature.name} - {tier.adversaryFeature.type}:</em></strong>{' '}
                <strong>{tier.adversaryFeature.cost}</strong> {tier.adversaryFeature.text}
              </p>
            )}
            <DoomEvents events={tier.events} path={tier.title} open marks={marks} onToggle={toggle} />
          </div>
        ))}
      </div>
    </HandoutSheet>
  );
}
